import { useState, useRef } from 'react';
import SourceSheetLink from './SourceSheetLink';
import BatchCompletion from './BatchCompletion';

const REORDER_SHEET_URL =
  'https://docs.google.com/spreadsheets/d/1e7XOnJhcj5tT3VLG3MeqHSBnvFiVk16keih6dFRTM70/edit?gid=0#gid=0';

function localBatchDate() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

const GROUP_STYLES = {
  kits: 'text-emerald-700 bg-emerald-100',
  cards: 'text-indigo-700 bg-indigo-100',
  stands: 'text-purple-700 bg-purple-100',
};

/**
 * Reorder processing: upload the Capital One sheet CSV, validate, then either
 * download the transformed import file or push it straight to the
 * orders.re3gifts.com import tool (which creates orders + codes + art).
 */
export default function ReorderModal({ onClose, onComplete, secretKey, onNeedAuth, onAuthFailed }) {
  const [step, setStep] = useState('select'); // select → preview → imported
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmImport, setConfirmImport] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();
  const batchDate = localBatchDate();

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) setFile(f);
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/reorders/preview', { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to parse file');
      setPreview(data);
      setStep('preview');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('batchDate', batchDate);
      const res = await fetch('/api/reorders/generate', { method: 'POST', body: formData });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to generate CSV');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RE${batchDate}-upload.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!secretKey) {
      onNeedAuth();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('batchDate', batchDate);
      formData.append('secret', secretKey);
      const res = await fetch('/api/reorders/import', { method: 'POST', body: formData });
      const data = await res.json();
      if (res.status === 403) {
        onAuthFailed();
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Import failed');
      setImportResult(data);
      setStep('imported');
      if (onComplete) onComplete();
    } catch (err) {
      setError(err.message);
      setConfirmImport(false);
    } finally {
      setLoading(false);
    }
  };

  const groups = preview?.groups || {};
  const totalBatches = ['kits', 'cards', 'stands'].filter((g) => groups[g] > 0).length;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Process Reorders</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {step === 'select' && "Upload Capital One's reorder sheet (CSV)"}
              {step === 'preview' && 'Review orders and planned batches before importing'}
              {step === 'imported' && 'Orders sent to the import tool'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {step === 'select' && (
            <div className="flex flex-col items-center py-6">
              <div className="w-full max-w-md">
                <SourceSheetLink href={REORDER_SHEET_URL} tone="amber" label="to see the latest batches." />
              </div>
              <div
                onClick={() => fileRef.current?.click()}
                className="w-full max-w-md border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-amber-400 hover:bg-amber-50/30 transition-colors"
              >
                <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
                {file ? (
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-600">Click to select the reorder CSV</p>
                    <p className="text-xs text-gray-400 mt-1">Downloaded from Capital One's Google Sheet</p>
                  </>
                )}
              </div>
              <input ref={fileRef} type="file" accept=".csv" onChange={handleFileSelect} className="hidden" />
            </div>
          )}

          {step === 'preview' && preview && (
            <>
              {/* Planned batches */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                {['kits', 'cards', 'stands'].map((g) => (
                  <div
                    key={g}
                    className={`rounded-lg px-4 py-3 border ${
                      groups[g] > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200 opacity-60'
                    }`}
                  >
                    <p className="text-xs text-amber-700 font-medium capitalize">{g}</p>
                    <p className="text-sm font-bold font-mono text-gray-900 mt-1">RE{batchDate}_{g}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {groups[g] || 0} order{groups[g] !== 1 ? 's' : ''}
                    </p>
                  </div>
                ))}
              </div>

              {preview.totals.withIssues > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 mb-4 text-sm text-red-800">
                  <span className="font-semibold">Review required:</span> {preview.totals.withIssues} order
                  {preview.totals.withIssues !== 1 ? 's have' : ' has'} issues. Fix the sheet and re-upload —
                  reorder imports run all-or-nothing.
                </div>
              )}

              {preview.totals.withWarnings > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 mb-4 text-sm text-amber-800">
                  <span className="font-semibold">Heads up:</span> {preview.totals.withWarnings} order
                  {preview.totals.withWarnings !== 1 ? 's reuse' : ' reuses'} QR ids that already exist in the
                  codes table. This doesn't block the import — check the sheet if it looks unexpected.
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[38vh]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="border-b border-gray-200">
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Order</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Dealer</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Name</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Cards</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Stands</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Type</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Check</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {preview.orders.map((o) => (
                        <tr
                          key={o.orderNumber}
                          className={!o.valid ? 'bg-red-50' : o.warnings?.length ? 'bg-amber-50' : ''}
                        >
                          <td className="px-4 py-2 font-mono text-xs font-semibold text-gray-900">{o.orderNumber}</td>
                          <td className="px-4 py-2 font-mono text-xs text-gray-600">{o.dealerId}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">{o.dealerName || '—'}</td>
                          <td className="px-4 py-2 text-xs text-gray-600 tabular-nums">{o.cards}</td>
                          <td className="px-4 py-2 text-xs text-gray-600 tabular-nums">{o.checkIns + o.servicings}</td>
                          <td className="px-4 py-2 text-xs">
                            {o.group ? (
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${GROUP_STYLES[o.group]}`}>
                                {o.type} → {o.group}
                              </span>
                            ) : (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded text-red-700 bg-red-100">
                                unclassified
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-xs">
                            {o.valid && (
                              <span className="text-emerald-600 font-medium mr-1">Ready</span>
                            )}
                            {o.issues.map((issue, i) => (
                              <span key={`i${i}`} className="inline-block mr-1 mb-0.5 text-[10px] font-medium text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                                {issue}
                              </span>
                            ))}
                            {(o.warnings || []).map((warning, i) => (
                              <span key={`w${i}`} className="inline-block mr-1 mb-0.5 text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                {warning}
                              </span>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                {preview.totals.orders} orders · {preview.totals.codes} QR codes · type computed from
                composition (≥20 cards + both stands = kit; otherwise single-type split_ship)
              </p>
            </>
          )}

          {step === 'imported' && importResult && (
            <div className="flex flex-col items-center justify-center py-6">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Sent to Import Tool</h3>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 mb-4 text-xs text-emerald-800 space-y-1">
                <p>✓ Import tool created <span className="font-semibold">{importResult.imported.orders}</span> orders / <span className="font-semibold">{importResult.imported.products}</span> products</p>
                <p>✓ Batch{importResult.batches.length !== 1 ? 'es' : ''}: <span className="font-mono font-semibold">{importResult.batches.map((b) => b.label).join(', ')}</span></p>
                <p>✓ send_authorized → <span className="font-mono font-semibold">TRUE</span>, art generation kicked off</p>
              </div>
              {importResult.importErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 w-full max-w-lg">
                  <p className="text-xs font-semibold text-red-800 mb-1">Import tool reported errors:</p>
                  <ul className="text-xs text-red-700 font-mono space-y-0.5 max-h-32 overflow-y-auto">
                    {importResult.importErrors.map((e, i) => (
                      <li key={i}>{String(e)}</li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="text-xs text-gray-400 mb-4 max-w-md text-center">
                Art generation takes a minute or two — if the art check comes up short, give it a moment
                and re-check before emailing production.
              </p>

              <BatchCompletion
                batches={importResult.batches.map((b) => ({ label: b.label, orders: b.orders }))}
                batchDate={batchDate}
                secretKey={secretKey}
                onNeedAuth={onNeedAuth}
                onAuthFailed={onAuthFailed}
                onSent={onComplete}
              />
            </div>
          )}
        </div>

        {/* Import confirmation */}
        {step === 'preview' && confirmImport && (
          <div className="mx-6 mt-2 bg-emerald-50 border border-emerald-300 rounded-xl px-5 py-4 min-h-0 overflow-y-auto max-h-[35vh]">
            <p className="text-sm font-semibold text-emerald-900 mb-1.5">
              Send {preview.totals.orders} order{preview.totals.orders !== 1 ? 's' : ''} to the import tool?
            </p>
            <p className="text-xs text-emerald-800">
              This creates the orders in the backend ({totalBatches} batch{totalBatches !== 1 ? 'es' : ''}:{' '}
              {['kits', 'cards', 'stands'].filter((g) => groups[g] > 0).map((g) => `RE${batchDate}_${g}`).join(', ')}),
              sets send_authorized → <span className="font-mono font-semibold">TRUE</span>, and kicks off QR art
              generation. This cannot be un-done from this app.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 flex-shrink-0">
          {step === 'select' && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">
                Cancel
              </button>
              <button
                onClick={handlePreview}
                disabled={!file || loading}
                className="px-5 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Checking…' : 'Check File'}
              </button>
            </>
          )}
          {step === 'preview' && confirmImport && (
            <>
              <button
                onClick={() => setConfirmImport(false)}
                disabled={loading}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-40 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={loading}
                className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-40 transition-colors"
              >
                {loading ? 'Importing…' : `Yes — import ${preview.totals.orders} order${preview.totals.orders !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
          {step === 'preview' && !confirmImport && (
            <>
              <button
                onClick={() => { setStep('select'); setPreview(null); setError(null); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleDownload}
                disabled={loading || !preview.ok}
                title={!preview.ok ? 'Fix all issues first' : undefined}
                className="px-4 py-2 border border-gray-300 bg-white text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Download Import CSV
              </button>
              <button
                onClick={() => setConfirmImport(true)}
                disabled={loading || !preview.ok}
                title={!preview.ok ? 'Fix all issues first' : undefined}
                className="px-5 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Send to Import Tool…
              </button>
            </>
          )}
          {step === 'imported' && (
            <button
              onClick={onClose}
              className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
